// Libraries
import React, { useMemo, useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { AttachMoney, Edit, Search as SearchIcon } from '@mui/icons-material'
import { Box, Divider, Button, Grid, Tabs, Tab, Typography, TextField, InputAdornment } from '@mui/material'
import { useTheme } from '@mui/styles'
import { styled } from '@mui/system'
import Dinero from 'dinero.js'

// RTK Queries
import { useGetMaterialTypesQuery } from 'services/materialTypes'

// State
import store from 'state/store'
import { showFormModal } from 'state/ui'

// Components
import DataGridWithSearchV2 from 'components/generic/datagrid/DataGridWithSearchV2'

// Helpers/Utils
import FORM_MODALS from 'utils/ui/modals'
import { commodityColumns } from 'components/containers/commodities/commoditiesHelpers'
import { setDecimalPrecision } from 'utils/helpers'
import { MEASURE_UNIT, MODAL_VIEW_MODES, PAGE_SIZE } from 'utils/constants'
import { useContentHeight } from 'utils/custom-hooks/useContentHeight'

const GridStyling = styled('div')(() => ({
  '& .MuiDataGrid-root': {
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
  },
}))

const CommoditiesListDetail = ({ selectedCommodityId, setSelectedCommodityId, commodities }) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const { contentHeight } = useContentHeight()
  const dispatch = useDispatch()

  const [searchQuery, setSearchQuery] = useState('')

  const filteredCommodities = commodities.filter(commodity =>
    commodity.name?.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  const [paginationOptions, setPaginationOptions] = useState({ page: 1, pageSize: PAGE_SIZE })
  const [filter, setFilter] = useState({
    search: '',
    commodityId: '',
    sort: {
      field: commodityColumns[0].field,
      ascending: true,
    },
  })

  const { settings, defaultYardUm } = useSelector(state => state.settings)
  const { disableUmDropdowns } = settings?.purchaseSettings?.inboundTickets || {}

  const { data, isFetching } = useGetMaterialTypesQuery(
    { filter: JSON.stringify(filter), ...paginationOptions },
    { skip: !selectedCommodityId },
  )

  const selectedCommodity = commodities.find(commodity => commodity.id === selectedCommodityId)

  const rows = useMemo(
    () =>
      (data?.Items || [])
        .filter(materialType => !!materialType.defaultPriceEntry)
        .map(materialType => {
          const { weightUnits = defaultYardUm, buyPrice, sellPrice } = materialType?.defaultPriceEntry || {}
          const finalWeightUnits = disableUmDropdowns ? defaultYardUm : weightUnits
          return {
            id: materialType.id,
            commonName: materialType.commonName,
            code: materialType.code,
            units: finalWeightUnits,
            buyPrice: `$${setDecimalPrecision(Dinero({ ...buyPrice?.price }).toUnit())}/${finalWeightUnits}`,
            sellPrice: `$${setDecimalPrecision(Dinero({ ...sellPrice?.price }).toUnit())}/${finalWeightUnits}`,
            commodityId: materialType.commodityId,
          }
        }),
    [data, disableUmDropdowns, defaultYardUm],
  )

  const handleTabClick = commodity => {
    if (commodity.id === selectedCommodityId) {
      setSelectedCommodityId(null)
    } else {
      setSelectedCommodityId(commodity.id)
      setFilter({ search: '', commodityId: commodity.id })
    }
  }

  const handleSortModelChange = useCallback(sortModel => {
    setFilter(previousFilter => ({
      ...previousFilter,
      sort: {
        field: sortModel?.[0]?.field,
        ascending: sortModel?.[0]?.sort === 'asc',
      },
    }))
  }, [])

  // Set the first commodity as selected by default
  useEffect(() => {
    if (commodities.length > 0 && !selectedCommodityId) {
      const firstCommodity = commodities[0]
      setSelectedCommodityId(firstCommodity.id)
      setFilter({ search: '', commodityId: firstCommodity.id })
    }
  }, [commodities, selectedCommodityId, setSelectedCommodityId])

  return (
    <Grid container pr={1}>
      <Grid item xs={3} px={1} sx={{ height: contentHeight }}>
        <Box
          sx={{
            border: 1,
            borderColor: 'grey.400',
            borderRadius: 2,
            backgroundColor: 'background.default',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
          pt={2}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 2,
              pb: 2,
              rowGap: 2,
            }}
          >
            <Grid container alignItems="center" flexDirection="row">
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => dispatch(showFormModal({ formModalName: FORM_MODALS.NEW_COMMODITY }))}
                >
                  Add Commodity
                </Button>
              </Grid>
              <Grid item xs={6} pl={1}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => dispatch(showFormModal({ formModalName: FORM_MODALS.NEW_MATERIAL }))}
                >
                  Add Material
                </Button>
              </Grid>
            </Grid>
            {/* <Button
              variant="outlined"
              fullWidth
              onClick={() => dispatch(showFormModal({ formModalName: FORM_MODALS.BENCHMARK_PRICING }))}
            >
              Benchmark Prices
            </Button> */}
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search Commodities"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              sx={{
                width: '100%',
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box
            sx={{
              overflowY: 'auto',
              flex: 1,
              pb: 2,
            }}
          >
            {filteredCommodities.length > 0 && (
              <Tabs orientation="vertical" value={selectedCommodityId || false} TabIndicatorProps={{ hidden: false }}>
                {filteredCommodities.map((commodity, index) => (
                  <Tab
                    key={commodity.id}
                    label={commodity.name}
                    value={commodity.id}
                    onClick={() => handleTabClick(commodity)}
                    sx={{
                      maxWidth: '100%',
                      borderTop: index === 0 ? 1 : 'unset',
                      borderBottom: 1,
                      borderColor: 'grey.400',
                      textTransform: 'none',
                      alignItems: 'flex-start',
                      padding: '24px 16px 24px 16px',
                      transition: 'background 0.2s ease-in-out',
                      fontWeight: '400',
                      color: theme.palette.text.primary,
                      '&:hover': {
                        backgroundColor: theme.palette.primary.lightBg,
                      },
                      '&.Mui-selected': {
                        backgroundColor: theme.palette.primary.lightBg,
                        color: theme.palette.text.primary,
                        fontWeight: '600',
                      },
                    }}
                  />
                ))}
              </Tabs>
            )}
          </Box>
        </Box>
      </Grid>
      <Grid
        item
        xs={9}
        p={3}
        sx={{
          border: 1,
          borderColor: 'grey.400',
          borderRadius: 2,
          backgroundColor: 'background.default',
          overflowY: 'auto',
          height: contentHeight,
        }}
        pt={4}
      >
        {selectedCommodityId && (
          <Box>
            <Grid container alignItems="center" mb={2}>
              <Grid item flexGrow={1}>
                <Typography variant="h6">{selectedCommodity.name}</Typography>
              </Grid>
              <Divider orientation="vertical" style={{ marginLeft: '-1px' }} />
              <Grid item flexGrow={0} style={{ paddingTop: '8px', paddingBottom: '8px' }}>
                <Button onClick={() => navigate(`${selectedCommodityId}`)}>
                  <AttachMoney /> Edit Pricing
                </Button>
                <Button
                  onClick={() =>
                    store.dispatch(
                      showFormModal({
                        formModalName: FORM_MODALS.NEW_COMMODITY,
                        formModalProps: { viewMode: MODAL_VIEW_MODES.EDIT, commodityId: selectedCommodityId },
                      }),
                    )
                  }
                >
                  <Edit /> Edit Commodity
                </Button>
              </Grid>
            </Grid>
            <GridStyling>
              <DataGridWithSearchV2
                columns={commodityColumns}
                rows={rows}
                loading={isFetching}
                useBackendSortAndFilter
                filter={filter}
                setFilter={setFilter}
                autoPageSize={false}
                paginationMode="server"
                sortingMode="server"
                sortingOrder={['asc', 'desc']}
                onSortModelChange={handleSortModelChange}
                initialState={{
                  sorting: {
                    sortModel: [{ field: commodityColumns[0].field, sort: 'asc' }],
                  },
                }}
                rowCount={data?.totalResultsCount || 0}
                onPageChange={newPageNumber =>
                  setPaginationOptions(prevState => ({ ...prevState, page: newPageNumber + 1 }))
                }
                onPageSizeChange={newPageSize =>
                  setPaginationOptions(prevState => ({ ...prevState, pageSize: newPageSize }))
                }
                pageSize={paginationOptions.pageSize}
                rowsPerPageOptions={[10, 20, 30, 50]}
              />
            </GridStyling>
          </Box>
        )}
      </Grid>
    </Grid>
  )
}

CommoditiesListDetail.propTypes = {
  selectedCommodityId: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  setSelectedCommodityId: PropTypes.func.isRequired,
  commodities: PropTypes.array.isRequired,
}

export default CommoditiesListDetail
